/**
 * YAML Type Detector
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { ITypeDetector } from '@/interfaces/ITypeDetector';

export const YamlDetector: ITypeDetector = {
  id: 'yaml-detector',
  name: 'YAML Detector',
  priority: 55, // Below JSON since YAML is harder to detect reliably
  detectedType: SEMANTIC_TYPES.YAML,

  detect(input: string): ISemanticType | null {
    const trimmed = input.trim();
    
    // Check for explicit YAML document markers
    if (trimmed.startsWith('---') || trimmed.startsWith('%YAML')) {
      return SEMANTIC_TYPES.YAML;
    }

    // YAML-like patterns
    const lines = trimmed.split('\n');
    
    // Must have at least 2 lines for reliable detection
    if (lines.length < 2) {
      return null;
    }

    // Count YAML-like patterns
    let yamlPatterns = 0;
    
    for (const line of lines) {
      const trimmedLine = line.trim();
      
      // Skip empty lines and comments
      if (!trimmedLine || trimmedLine.startsWith('#')) {
        if (trimmedLine.startsWith('#')) yamlPatterns++; // YAML comments
        continue;
      }

      // Key-value pattern: "key: value" or "key:"
      if (/^[\w.-]+:\s*.*/i.test(trimmedLine)) {
        yamlPatterns++;
        continue;
      }

      // List item: "- value"
      if (/^-\s+.+/.test(trimmedLine)) {
        yamlPatterns++;
        continue;
      }

      // Indented content (nested YAML)
      if (/^\s{2,}[\w.-]+:/.test(line)) {
        yamlPatterns++;
        continue;
      }
    }

    // If more than 50% of non-empty lines look like YAML, it's probably YAML
    const nonEmptyLines = lines.filter(l => l.trim() && !l.trim().startsWith('#')).length;
    if (nonEmptyLines > 0 && yamlPatterns / nonEmptyLines >= 0.5) {
      // Make sure it's not valid JSON (JSON is preferred)
      try {
        JSON.parse(trimmed);
        return null; // It's JSON, not YAML
      } catch {
        return SEMANTIC_TYPES.YAML;
      }
    }

    return null;
  },
};
